import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, linkedSignal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { AccountType } from "@front/m19-api-client";
import { AccountSelectionService } from "@front/m19-services";
import { ToasterService } from "@front/m19-ui";
import { Utils } from "@front/m19-utils";
import { TranslocoService } from "@jsverse/transloco";
import { SbWizardPageComponent } from "@m19-board/strategies/sb-create-strategy/sb-wizard-page/sb-wizard-page.component";
import { SbWizardCreationService } from "@m19-board/strategies/sb-create-strategy/shared/sb-wizard-creation.service";
import { createSbWizardForm } from "@m19-board/strategies/sb-create-strategy/shared/sb-wizard-form";
import {
  assetsDefaultFormModel,
  creativesSbAsinsDefaultFormModel,
  formatDefaultFormModel,
  setupDefaultFormModel,
} from "@m19-board/strategies/sb-create-strategy/shared/sb-wizard-form.defaults";
import {
  CREATE_STRATEGY_STEP_IDS,
  SB_STRATEGIES_URL,
  SbWizardFormModel,
} from "@m19-board/strategies/sb-create-strategy/shared/sb-wizard-types";

@Component({
  selector: "app-sb-create-strategy-page",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SbWizardCreationService],
  imports: [SbWizardPageComponent],
  template: ` <app-sb-wizard-page [form]="creationForm" [steps]="steps" (cancelled)="cancel()" /> `,
})
export class SbCreateStrategyPage {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly sbWizardCreationService = inject(SbWizardCreationService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  private readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });
  private readonly isVendor = computed(() => this.accountMarketplace().accountType === AccountType.VENDOR);

  readonly steps = CREATE_STRATEGY_STEP_IDS;

  private readonly formModel = linkedSignal<SbWizardFormModel>(() => ({
    setup: {
      ...setupDefaultFormModel,
      name: Utils.generateRandomName(),
    },
    format: formatDefaultFormModel,
    assets: assetsDefaultFormModel,
    creativesSbAsins: creativesSbAsinsDefaultFormModel,
  }));

  readonly creationForm = createSbWizardForm({
    formModel: this.formModel,
    isVendor: this.isVendor,
    marketplace: computed(() => this.accountMarketplace().marketplace),
    submitForm: async () => this.submit(),
  });

  submit() {
    this.sbWizardCreationService
      .createStrategyWithCreative(this.creationForm, this.isVendor())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (creative) => {
          this.toasterService.success(this.translocoService.translate("sb-form-creative.sb_strategy_created"));
          void this.router.navigate(["..", creative.strategyId], {
            relativeTo: this.route,
            queryParamsHandling: "merge",
          });
        },
        error: (err) => {
          this.toasterService.error(err);
        },
      });
  }

  cancel(): void {
    void this.router.navigate([SB_STRATEGIES_URL]);
  }
}
